import type { ReactNode } from 'react';
import { AlertTriangle, Check, Hammer, Plus, UserCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { PersonFace } from '@/components/work/WorkLockScreen';
import { WorkerChip, QuietEmpty } from './shared';
import { formatDateOnly } from '@/lib/formatters';
import { SIGNOFF_STATE_META, matrixKey, type SignoffState } from '@/features/workMode/signoffs';
import type { FloorWorkstation, StationInstructionHash, WorkstationSignoff } from '@/features/workMode/types';
import type { WorkLookups, WorkPerson } from '@/features/workMode/useLookups';

/** Everything the matrix views render, derived once by the page. */
export interface MatrixModel {
  stations: FloorWorkstation[];
  people: WorkPerson[];
  /** `${stationId}:${userId}` → the active sign-off. */
  active: Map<string, WorkstationSignoff>;
  /** Active sign-offs per station, by person name. */
  byStation: Map<string, WorkstationSignoff[]>;
  /** Active sign-offs per person, in station order. */
  byPerson: Map<string, WorkstationSignoff[]>;
  /** Sign-off id → state. */
  states: Map<string, SignoffState>;
  hashes: Map<string, StationInstructionHash> | undefined;
}

export function SignoffStateBadge({ state }: { state: SignoffState }) {
  const meta = SIGNOFF_STATE_META[state];
  return (
    <Badge variant={meta.variant}>
      {state === 'changed' && <AlertTriangle className="mr-1 h-3 w-3" />}
      {state === 'current' && <Check className="mr-1 h-3 w-3" />}
      {meta.label}
    </Badge>
  );
}

function instructionSummary(live: StationInstructionHash | undefined): string | null {
  if (!live) return null;
  if (live.set_count === 0) return 'No published instructions';
  return `${live.set_count} instruction set${live.set_count === 1 ? '' : 's'} · ${live.step_count} step${live.step_count === 1 ? '' : 's'}`;
}

/** Pill for one sign-off: a face (or station icon), a label and the date. */
function SignoffChip({
  leading,
  label,
  date,
  state,
  onClick,
}: {
  leading: ReactNode;
  label: string;
  date: string;
  state: SignoffState;
  onClick: () => void;
}) {
  const changed = state === 'changed';
  return (
    <button
      type="button"
      onClick={onClick}
      title={changed ? `${label} — instructions changed since sign-off` : label}
      className={cn(
        'inline-flex min-h-11 max-w-full items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-left transition-colors md:min-h-9',
        'hover:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        changed ? 'border-fresh-salmon/60 bg-fresh-salmon/10' : 'border-border bg-background',
      )}
    >
      {leading}
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium leading-tight text-foreground">{label}</span>
        <span className={cn('flex items-center gap-1 text-[11px] leading-tight', changed ? 'text-fresh-salmon-dark' : 'text-muted-foreground')}>
          {changed && <AlertTriangle className="h-3 w-3 shrink-0" />}
          {formatDateOnly(date)}
        </span>
      </span>
    </button>
  );
}

/**
 * The root of the matrix: every workstation and the people signed off on
 * it. Tapping a person opens their record with the copied instructions.
 */
export function StationSignoffList({
  model,
  lookups,
  canManage,
  onOpen,
  onSignOff,
}: {
  model: MatrixModel;
  lookups: WorkLookups;
  canManage: boolean;
  onOpen: (signoffId: string) => void;
  onSignOff: (stationId: string) => void;
}) {
  if (model.stations.length === 0) {
    return <QuietEmpty>No workstations yet. Add them under Manufacturing → Workstations.</QuietEmpty>;
  }
  return (
    <div className="divide-y divide-border rounded-2xl border border-border bg-card">
      {model.stations.map((station) => {
        const rows = model.byStation.get(station.id) ?? [];
        const summary = instructionSummary(model.hashes?.get(station.id));
        const noInstructions = model.hashes?.get(station.id)?.set_count === 0;
        return (
          <section key={station.id} className="flex flex-col gap-3 p-4 md:flex-row md:items-start">
            <div className="flex items-start justify-between gap-3 md:w-64 md:shrink-0">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Hammer className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <h3 className="truncate text-sm font-medium text-foreground">{station.name}</h3>
                  {!station.is_active && <Badge variant="muted">Inactive</Badge>}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {rows.length} signed off
                  {summary && (
                    <>
                      {' · '}
                      <span className={cn(noInstructions && 'text-fresh-salmon-dark')}>{summary}</span>
                    </>
                  )}
                </p>
              </div>
              {canManage && (
                <Button size="sm" variant="outline" className="shrink-0 md:hidden" onClick={() => onSignOff(station.id)}>
                  <UserCheck className="h-4 w-4" />
                  Sign off
                </Button>
              )}
            </div>
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
              {rows.length === 0 ? (
                <span className="text-sm text-muted-foreground">No one signed off yet</span>
              ) : (
                rows.map((s) => {
                  const person = lookups.person(s.user_id);
                  return (
                    <SignoffChip
                      key={s.id}
                      leading={<PersonFace person={person} size={28} className="shrink-0" />}
                      label={person.name}
                      date={s.signed_off_on}
                      state={model.states.get(s.id) ?? 'current'}
                      onClick={() => onOpen(s.id)}
                    />
                  );
                })
              )}
            </div>
            {canManage && (
              <Button size="sm" variant="outline" className="hidden shrink-0 self-start md:inline-flex" onClick={() => onSignOff(station.id)}>
                <UserCheck className="h-4 w-4" />
                Sign off
              </Button>
            )}
          </section>
        );
      })}
    </div>
  );
}

/** Desktop grid: one row per person, one column per workstation. */
export function SignoffMatrixGrid({
  model,
  people,
  canManage,
  onOpen,
  onSignOff,
}: {
  model: MatrixModel;
  people: WorkPerson[];
  canManage: boolean;
  onOpen: (signoffId: string) => void;
  onSignOff: (stationId: string, userId: string) => void;
}) {
  if (model.stations.length === 0) {
    return <QuietEmpty>No workstations yet. Add them under Manufacturing → Workstations.</QuietEmpty>;
  }
  if (people.length === 0) return <QuietEmpty>Nobody matches.</QuietEmpty>;
  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            <th scope="col" className="sticky left-0 z-10 min-w-[220px] bg-card px-4 py-3 text-left align-bottom text-xs font-medium text-muted-foreground">
              Employee
            </th>
            {model.stations.map((s) => (
              <th key={s.id} scope="col" className="min-w-[104px] max-w-[150px] border-l border-border px-2 py-3 text-center align-bottom">
                <span className="line-clamp-2 text-xs font-medium text-foreground" title={s.name}>
                  {s.name}
                </span>
                <span className="text-[11px] font-normal tabular-nums text-muted-foreground">{(model.byStation.get(s.id) ?? []).length} signed off</span>
              </th>
            ))}
            <th scope="col" className="border-l border-border px-3 py-3 text-right align-bottom text-xs font-medium text-muted-foreground">
              Stations
            </th>
          </tr>
        </thead>
        <tbody>
          {people.map((p) => {
            const count = (model.byPerson.get(p.id) ?? []).length;
            return (
              <tr key={p.id} className="border-t border-border first:border-t-0">
                <th scope="row" className="sticky left-0 z-10 bg-card px-4 py-2 text-left font-normal">
                  <WorkerChip person={p} size={28} sub={p.jobTitle} />
                </th>
                {model.stations.map((s) => {
                  const signoff = model.active.get(matrixKey(s.id, p.id));
                  return (
                    <td key={s.id} className="border-l border-border p-1 text-center">
                      {signoff ? (
                        <MatrixMark
                          state={model.states.get(signoff.id) ?? 'current'}
                          date={signoff.signed_off_on}
                          label={`${p.name} at ${s.name}`}
                          onClick={() => onOpen(signoff.id)}
                        />
                      ) : canManage ? (
                        <button
                          type="button"
                          onClick={() => onSignOff(s.id, p.id)}
                          aria-label={`Sign off ${p.name} at ${s.name}`}
                          title={`Sign off ${p.name} at ${s.name}`}
                          className="group flex h-12 w-full items-center justify-center rounded-lg text-muted-foreground/40 hover:bg-secondary/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-border group-hover:hidden group-focus-visible:hidden" />
                          <Plus className="hidden h-4 w-4 group-hover:block group-focus-visible:block" />
                        </button>
                      ) : (
                        <span className="flex h-12 items-center justify-center" aria-label="Not signed off">
                          <span className="h-1.5 w-1.5 rounded-full bg-border" />
                        </span>
                      )}
                    </td>
                  );
                })}
                <td className="border-l border-border px-3 text-right tabular-nums text-muted-foreground">
                  {count}/{model.stations.length}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MatrixMark({ state, date, label, onClick }: { state: SignoffState; date: string; label: string; onClick: () => void }) {
  const changed = state === 'changed';
  return (
    <button
      type="button"
      onClick={onClick}
      title={changed ? `${label} — instructions changed since sign-off` : label}
      aria-label={`${label}, signed off ${formatDateOnly(date)}${changed ? ', instructions changed since' : ''}`}
      className={cn(
        'flex h-12 w-full flex-col items-center justify-center gap-0.5 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        changed ? 'bg-fresh-salmon/15 hover:bg-fresh-salmon/25' : 'bg-bright-moss/20 hover:bg-bright-moss/35',
      )}
    >
      <span
        className={cn(
          'flex h-5 w-5 items-center justify-center rounded-full text-linen',
          changed ? 'bg-fresh-salmon-dark' : 'bg-bright-moss-dark',
        )}
      >
        {changed ? <AlertTriangle className="h-3 w-3" /> : <Check className="h-3 w-3" />}
      </span>
      <span className={cn('text-[10px] tabular-nums leading-none', changed ? 'text-fresh-salmon-dark' : 'text-bright-moss-dark')}>
        {formatDateOnly(date)}
      </span>
    </button>
  );
}

/** Phone version of the grid: one card per person with their stations. */
export function PeopleSignoffList({
  model,
  people,
  onOpen,
}: {
  model: MatrixModel;
  people: WorkPerson[];
  onOpen: (signoffId: string) => void;
}) {
  if (people.length === 0) return <QuietEmpty>Nobody matches.</QuietEmpty>;
  const stationNames = new Map(model.stations.map((s) => [s.id, s.name]));
  return (
    <div className="flex flex-col gap-2">
      {people.map((p) => {
        const rows = model.byPerson.get(p.id) ?? [];
        return (
          <div key={p.id} className="rounded-lg border border-border bg-card px-3.5 py-3">
            <WorkerChip person={p} sub={`${rows.length} of ${model.stations.length} station${model.stations.length === 1 ? '' : 's'}`} />
            {rows.length > 0 && (
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {rows.map((s) => (
                  <SignoffChip
                    key={s.id}
                    leading={
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground">
                        <Hammer className="h-3.5 w-3.5" />
                      </span>
                    }
                    label={(s.workstation_id && stationNames.get(s.workstation_id)) || s.workstation_name}
                    date={s.signed_off_on}
                    state={model.states.get(s.id) ?? 'current'}
                    onClick={() => onOpen(s.id)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
